import {useState, useEffect, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import * as PropTypes from 'prop-types'
import {Button, getAsset, IconWithBorder, LoadingCircle, useAssetAmounts} from '@components/lib'
import {generatePath, useLocation} from 'react-router-dom'
import {Tooltip} from '@mui/material'

import {cacheKeys} from 'api'
import {useProfile} from 'hooks'
import {TokenInfo} from 'constData'
import routes from 'routes/pathConfig'
import {isProfileConfirmed} from 'utils'
import useIsCryptoApiWorking from 'hooks/helpers/useIsCryptoApiWorking'
import useWalletConfig from 'hooks/helpers/useWalletConfig'
import useQuotes from 'hooks/helpers/useQuotes'
import useAssetGenerateWallet from 'hooks/helpers/useAssetGenerateWallet'
import useAssetQuery from 'hooks/helpers/useAssetQuery'
import useAssetLink from 'hooks/helpers/useAssetLink'
import ModalInfo from 'components/modals/ModalInfo'
import useTokenConvertQuery from 'hooks/helpers/useTokenConvertQuery'
import useRefetchQuery from 'hooks/helpers/useRefetchQuery'
import ModalTokenTransfer from 'components/modals/ModalTokenTransfer'

import {
  Wrapper,
  CurrencyWrapper,
  ActionsWrapper,
  ComingSoonText,
  SmallText,
} from './AssetRow.styles'
import AssetRowCell from './AssetRowCell'

const AssetRow = ({apiId, hideSmallBalance}) => {
  const {t} = useTranslation()
  const location = useLocation()
  const {profile} = useProfile()
  const isConfirmed = isProfileConfirmed(profile)
  const {getCachedData} = useRefetchQuery()

  const {color, Icon, name, urlId} = getAsset(apiId)

  const {canWithdraw, canDeposit, canBuy, comingSoon, disabled, enabled} = useWalletConfig(apiId)
  const {generateWallet, isLoading: isLoadingGenerate} = useAssetGenerateWallet(apiId)
  const {isCryptoApiWorking} = useIsCryptoApiWorking(apiId)
  const [successOpened, setSuccessOpened] = useState(false)
  const [ethData, setEthData] = useState({})
  const [usdtData, setUsdtData] = useState({})
  const [tokenSuccessOpened, setTokenSuccessOpened] = useState(false)
  const [mailDomainAlertOpened, setMailDomainAlertOpened] = useState(false)
  // asset info hooks
  const assetQuery = useAssetQuery(apiId)

  const assetLink = useAssetLink(apiId)
  const assetAmounts = useAssetAmounts(assetQuery.data)
  const {getPriceString, calculatePrice} = useQuotes(apiId)
  const availablePrice = calculatePrice(assetAmounts.available)

  const {data: tokenConvertEligibility} = useTokenConvertQuery(apiId)

  useEffect(() => {
    if (name === TokenInfo.MBASE.apiId) {
      const refetchEth = getCachedData([cacheKeys.asset, 'ETH'])
      const refetchUsdt = getCachedData([cacheKeys.asset, 'USDT'])
      setEthData(refetchEth)
      setUsdtData(refetchUsdt)
    }
    // eslint-disable-next-line
  }, [name, assetQuery])
  const availableEth = useAssetAmounts(ethData)
  const availableUsdt = useAssetAmounts(usdtData)

  const isValidMailDomain = useMemo(() => {
    if (profile?.email.includes('yopmail')) {
      return false
    } else {
      return true
    }
    // eslint-disable-next-line
  }, [assetQuery, profile?.email])

  const openAlertModal = useCallback(() => {
    if (availableEth?.available <= 0 && availableUsdt?.available <= 0) {
      return setSuccessOpened(true)
    } else if (!isValidMailDomain) {
      return setMailDomainAlertOpened(true)
    }
  }, [isValidMailDomain, availableEth?.available, availableUsdt?.available])

  if (!enabled || (hideSmallBalance && !assetQuery.isLoading && availablePrice < 1)) {
    return null
  }

  return (
    <Wrapper>
      <AssetRowCell to={assetLink}>
        <CurrencyWrapper>
          <IconWithBorder Icon={Icon} color={color} size='xxs' />
          <span>{name === TokenInfo.MBASE.apiId ? 'CREATOR WALLET' : name}</span>
        </CurrencyWrapper>
      </AssetRowCell>

      {assetQuery.isLoading ? (
        <AssetRowCell colSpan={3}>
          <LoadingCircle width='20px' thickness={4} color={color} style={{margin: 0}} />
        </AssetRowCell>
      ) : (
        <>
          <AssetRowCell to={assetLink}>
            <div>
              <div>{assetAmounts.availableStr}</div>
              <SmallText>{getPriceString(assetAmounts.available)}</SmallText>
            </div>
          </AssetRowCell>
          <AssetRowCell to={assetLink}>
            <div>
              <div>{assetAmounts.totalStr}</div>
              <SmallText>{getPriceString(assetAmounts.total)}</SmallText>
            </div>
          </AssetRowCell>

          <AssetRowCell to={assetLink}>
            <ActionsWrapper>
              {name === TokenInfo.V999.apiId || name === TokenInfo.GNS.apiId ? (
                assetAmounts.available > 0 &&
                tokenConvertEligibility?.convert_eligible &&
                (tokenConvertEligibility?.convert_to === 'product' ? (
                  <Button
                    size='sm'
                    nature='primary'
                    w100
                    to={routes.tokenConvert.path}
                    text={t('wallet.buttons.convertToken')}
                    disabled={!isConfirmed}
                  />
                ) : (
                  <Button
                    size='sm'
                    nature='primary'
                    w100
                    onClick={() => setTokenSuccessOpened(true)}
                    text={t('wallet.buttons.convertToken')}
                    disabled={!isConfirmed}
                  />
                ))
              ) : assetQuery.data ? (
                <>
                  {canDeposit &&
                    isCryptoApiWorking &&
                    isConfirmed &&
                    (!comingSoon && !disabled && assetQuery?.data?.address ? (
                      <Button
                        text={t('wallet.buttons.deposit', {tokenName: ''})}
                        size='sm'
                        minWidth='90px'
                        to={assetLink}
                        state={{openDepositModal: true}}
                        style={
                          canWithdraw && canBuy
                            ? {width: '33%'}
                            : canWithdraw || canBuy
                              ? {width: '50%'}
                              : {width: '100%'}
                        }
                      />
                    ) : (
                      (name === TokenInfo.MBASE.apiId ||
                        name === 'ETH' ||
                        name === 'USDT' ||
                        name === 'XON') && (
                        <Button
                          w100
                          size='sm'
                          nature='semiTransparent'
                          onClick={isConfirmed ? generateWallet : undefined}
                          isLoading={isLoadingGenerate}
                          text={t('wallet.buttons.generateWallet')}
                          disabled={!isConfirmed}
                          as={isConfirmed ? undefined : 'div'}
                        />
                      )
                    ))}
                  {canWithdraw &&
                    isCryptoApiWorking &&
                    isConfirmed &&
                    assetQuery?.data?.address &&
                    (name === TokenInfo.MBASE.apiId ? (
                      (availableEth.available > 0 || availableUsdt?.available > 0) &&
                      isValidMailDomain ? (
                        <Button
                          text={t('wallet.buttons.withdraw', {tokenName: ''})}
                          size='sm'
                          minWidth='90px'
                          nature='primary'
                          to={{
                            pathname: generatePath(routes.wallet.info.withdraw.path, {id: urlId}),
                            state: {
                              from: location,
                            },
                          }}
                          style={
                            canDeposit && canBuy
                              ? {marginLeft: '8px', width: '33%'}
                              : canDeposit || canBuy
                                ? {marginLeft: '8px', width: '50%'}
                                : {width: '100%'}
                          }
                        />
                      ) : (
                        <Button
                          text={t('wallet.buttons.withdraw', {tokenName: ''})}
                          size='sm'
                          minWidth='90px'
                          nature='primary'
                          onClick={openAlertModal}
                          style={
                            canDeposit && canBuy
                              ? {marginLeft: '8px', width: '33%'}
                              : canDeposit || canBuy
                                ? {marginLeft: '8px', width: '50%'}
                                : {width: '100%'}
                          }
                        />
                      )
                    ) : (
                      <Button
                        text={t('wallet.buttons.withdraw', {tokenName: ''})}
                        size='sm'
                        minWidth='90px'
                        nature='primary'
                        to={{
                          pathname: generatePath(routes.wallet.info.withdraw.path, {id: urlId}),
                          state: {
                            from: location,
                          },
                        }}
                        style={
                          canDeposit && canBuy
                            ? {marginLeft: '8px', width: '33%'}
                            : canDeposit || canBuy
                              ? {marginLeft: '8px', width: '50%'}
                              : {width: '100%'}
                        }
                      />
                    ))}
                  {canBuy &&
                    isCryptoApiWorking &&
                    isConfirmed &&
                    assetQuery?.data?.address &&
                    (name === TokenInfo.MBASE.apiId || name === 'XON' || name === 'VIME') && (
                      <Button
                        text={t('wallet.buttons.buy', {tokenName: ''})}
                        size='sm'
                        minWidth='90px'
                        nature='primary'
                        to={{
                          pathname: assetLink,
                          search: 'type=purchaseToken',
                        }}
                        style={
                          canDeposit && canWithdraw
                            ? {marginLeft: '8px', width: '33%'}
                            : canDeposit || canWithdraw
                              ? {marginLeft: '8px', width: '50%'}
                              : {width: '100%'}
                        }
                      />
                    )}
                  {comingSoon && <ComingSoonText>{t('wallet.comingSoon')}</ComingSoonText>}
                  {disabled && <ComingSoonText>{t('wallet.currentlyDisabled')}</ComingSoonText>}
                </>
              ) : (
                (name === TokenInfo.MBASE.apiId ||
                  name === 'ETH' ||
                  name === 'USDT' ||
                  name === 'XON' ||
                  name === 'VIME') && (
                  <div style={{width: '100%'}}>
                    <Tooltip
                      title={isConfirmed ? '' : 'Complete register process first'}
                      arrow
                      placement='top'
                    >
                      <div>
                        <Button
                          size='sm'
                          nature='semiTransparent'
                          onClick={isConfirmed ? generateWallet : undefined}
                          isLoading={isLoadingGenerate}
                          text={t('wallet.buttons.generateWallet')}
                          minWidth='140px'
                          w100
                          disabled={!isConfirmed}
                          as={isConfirmed ? undefined : 'div'}
                        />
                      </div>
                    </Tooltip>
                  </div>
                )
              )}
            </ActionsWrapper>
          </AssetRowCell>
        </>
      )}
      <ModalInfo
        title={t('wallet.withdrawInfo.paymentBalanceAlert.title')}
        description={t('wallet.withdrawInfo.paymentBalanceAlert.description')}
        isOpened={successOpened}
        onClose={() => setSuccessOpened(false)}
      />
      <ModalInfo
        title={t('wallet.withdrawInfo.invalidMailDomainAlert.title')}
        description={t('wallet.withdrawInfo.invalidMailDomainAlert.description')}
        isOpened={mailDomainAlertOpened}
        onClose={() => setMailDomainAlertOpened(false)}
      />
      <ModalTokenTransfer
        title={t('wallet.assets.tokenTransferTitle')}
        convertData={{...tokenConvertEligibility, availableToken: assetAmounts.available}}
        isOpened={tokenSuccessOpened}
        onClose={() => setTokenSuccessOpened(false)}
      />
    </Wrapper>
  )
}

AssetRow.propTypes = {
  apiId: PropTypes.string.isRequired,
  hideSmallBalance: PropTypes.bool,
}
AssetRow.defaultProps = {
  hideSmallBalance: false,
}

export default AssetRow
